import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { Tooltip } from '@mui/material';
import ContentPanel from '../../../../components/shared/ContentPanel';
import { ContentPanelProvider } from '../../../../contexts/ContentPanelContext';
import { InspectorProvider } from '../../../../contexts/InspectorContext';
import { tooltipSxElevated } from '../../../../components/shared/jernyTooltipStyling';
import SearchDrawer from './SearchDrawer/SearchDrawer';
import useSelectedEntry from './useSelectedEntry';

const DRAWER_MIN  = 180;
const DRAWER_MAX  = 520;
const DRAWER_DEFAULT = 280;

/**
 * FloatingContentPanelBody — functional inner component of FloatingContentPanel.
 * Wrapped in React.memo — panelId and initialQuery are stable during drag/resize
 * so the body tree doesn't re-render when only pos/size/zIndex changes in the parent.
 *
 * Owns:
 * - selectedEntry via useSelectedEntry(panelId)
 * - pinned/open state for SearchDrawer slide behaviour
 * - drawerWidth — shared between spacer + SearchDrawer so slide and pinned mode stay in sync
 * - click-outside detection to close drawer when unpinned
 *
 * Tooltips: MUI Tooltip + tooltipSxElevated (components/shared/jernyTooltipStyling.js) —
 * niet JxTooltip (deprecated 2026-07-08, zie Development.Patterns rk=ed2d2d36-15de-4867-8b09-552a7801b626).
 *
 * @param {string} props.panelId
 * @param {string} props.initialQuery
 */
const FloatingContentPanelBody = memo(function FloatingContentPanelBody({ panelId, initialQuery }) {
    const selectedEntry              = useSelectedEntry(panelId);
    const [open, setOpen]            = useState(false);
    const [pinned, setPinned]        = useState(true);
    const [drawerWidth, setDrawerWidth] = useState(DRAWER_DEFAULT);
    const bodyRef                    = useRef(null);

    // Close drawer on click outside when unpinned + open
    useEffect(() => {
        if (pinned || !open) return;
        const handleClick = (e) => {
            if (bodyRef.current && !bodyRef.current.querySelector('.sd-root')?.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [pinned, open]);

    // Splitter drag — mousedown on sd-splitter starts a document-level drag
    const handleSplitterMouseDown = useCallback((e) => {
        e.preventDefault();
        const startX     = e.clientX;
        const startWidth = drawerWidth;

        const onMouseMove = (ev) => {
            const next = Math.min(DRAWER_MAX, Math.max(DRAWER_MIN, startWidth + ev.clientX - startX));
            setDrawerWidth(next);
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup',  onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup',   onMouseUp);
    }, [drawerWidth]);

    const handleTogglePin = (e) => {
        e.stopPropagation();
        setPinned((v) => !v);
        setOpen(false);
    };

    const drawerVisible = pinned || open;
    const drawerClass   = ['sd-root', drawerVisible ? 'sd-root--open' : ''].filter(Boolean).join(' ');

    return (
        <div id={`fcp-body-${panelId}`} className="fcp-body" ref={bodyRef}>

            {/* Click strip — only rendered when unpinned, 6px handle on left edge */}
            {!pinned && (
                <Tooltip title="Slide Search open" placement="right" componentsProps={tooltipSxElevated}>
                    <div
                        className="fcp-hover-strip"
                        onClick={() => setOpen((v) => !v)}
                    />
                </Tooltip>
            )}

            {/* Spacer — same width as drawer, pushes content right when pinned */}
            {pinned && <div className="fcp-drawer-spacer" style={{ width: drawerWidth }} />}

            {/* SearchDrawer — always mounted, never remounts */}
            <SearchDrawer
                panelId={panelId}
                initialQuery={initialQuery}
                drawerClass={drawerClass}
                drawerWidth={drawerWidth}
                pinned={pinned}
                onTogglePin={handleTogglePin}
                onSplitterMouseDown={handleSplitterMouseDown}
            />

            {/* Content zone — flex column so ContentPanel fills full height */}
            <div id={`fcp-content-${panelId}`} className="fcp-content">
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }}>
                    <InspectorProvider>
                        <ContentPanelProvider>
                            <ContentPanel entry={selectedEntry} panelId={panelId} />
                        </ContentPanelProvider>
                    </InspectorProvider>
                </div>
            </div>
        </div>
    );
});

export default FloatingContentPanelBody;
